/**
 * Social media handle / URL utilities.
 *
 * We store whatever the admin enters — short handles like `@repbyrondonalds`
 * OR full URLs like `https://instagram.com/repbyrondonalds`. The two
 * helpers below normalize on read so the UI can render a clean display
 * label and a working clickable link regardless of which format the
 * value was saved in.
 *
 * Why store both formats?
 *   - AI enrichment prefers the compact handle form (`@handle`) because
 *     it's friendlier to users skimming the modal preview.
 *   - Hand-entered URLs (e.g., a campaign page that's NOT at the
 *     canonical `/{handle}` path) need to survive untouched.
 */

// Base URLs for converting `@handle` → clickable link, per platform.
const PLATFORM_BASE = {
  twitter: 'https://twitter.com/',     // also covers x.com — canonical to twitter
  x: 'https://twitter.com/',
  facebook: 'https://www.facebook.com/',
  instagram: 'https://www.instagram.com/',
  linkedin: 'https://www.linkedin.com/in/',
  youtube: 'https://www.youtube.com/@',
  tiktok: 'https://www.tiktok.com/@',
  threads: 'https://www.threads.net/@',
  bluesky: 'https://bsky.app/profile/',
  mastodon: 'https://mastodon.social/@',
  website: 'https://',
};

// Reverse map: domain → platform (used by displayHandle to recognize
// canonical profile URLs and shorten them).
const DOMAIN_TO_PLATFORM = {
  'twitter.com': 'twitter',
  'x.com': 'twitter',
  'www.twitter.com': 'twitter',
  'www.x.com': 'twitter',
  'facebook.com': 'facebook',
  'www.facebook.com': 'facebook',
  'instagram.com': 'instagram',
  'www.instagram.com': 'instagram',
  'linkedin.com': 'linkedin',
  'www.linkedin.com': 'linkedin',
  'youtube.com': 'youtube',
  'www.youtube.com': 'youtube',
  'tiktok.com': 'tiktok',
  'www.tiktok.com': 'tiktok',
  'threads.net': 'threads',
  'www.threads.net': 'threads',
};

/**
 * Resolve the given social-media value into a clickable URL.
 *
 * @param {string} platform — 'twitter' | 'facebook' | ... (case-insensitive)
 * @param {string} value    — `@handle` or full URL or just `handle`
 * @returns {string} a fully-qualified https URL, or '' when value is empty.
 */
export function socialMediaUrl(platform, value) {
  if (!value) return '';
  const v = String(value).trim();
  if (!v) return '';
  // Already a full URL — return as-is.
  if (/^https?:\/\//i.test(v)) return v;
  const key = String(platform || '').toLowerCase();
  const base = PLATFORM_BASE[key] || 'https://';
  // Strip a leading '@' before joining so we don't double-up: '@@handle'.
  const handle = v.startsWith('@') ? v.slice(1) : v;
  // Avoid building https://...//handle if the base already ends with '/'.
  return base + handle;
}

/**
 * Build a compact display label for a social-media value.
 *  - `@repbyrondonalds`                            → `@repbyrondonalds`
 *  - `https://instagram.com/repbyrondonalds`       → `@repbyrondonalds`
 *  - `https://campaign.com/pages/foo`              → `https://campaign.com/pages/foo`
 *  - `repbyrondonalds`                             → `@repbyrondonalds`
 *
 * This makes long profile URLs human-friendly without losing the
 * underlying link target (which `socialMediaUrl` derives separately).
 */
export function socialMediaDisplay(platform, value) {
  if (!value) return '';
  const v = String(value).trim();
  if (!v) return '';
  if (v.startsWith('@')) return v;
  // Full URL — try to recognize known platform domains and reduce to @handle.
  const urlMatch = v.match(/^https?:\/\/([^/]+)(\/.*)?$/i);
  if (urlMatch) {
    const host = urlMatch[1].toLowerCase();
    const path = urlMatch[2] || '';
    if (DOMAIN_TO_PLATFORM[host]) {
      // Treat the first non-empty path segment as the handle. Skip the
      // canonical `@` prefix YouTube uses internally.
      const seg = path.split('/').filter(Boolean)[0];
      if (seg) {
        const handle = seg.startsWith('@') ? seg.slice(1) : seg;
        return '@' + handle;
      }
    }
    // Non-canonical profile URL — return the full URL unchanged so we
    // don't silently mislabel an arbitrary path.
    return v;
  }
  // Bare handle without '@' (rare) — add it for visual consistency.
  return '@' + v;
}

/**
 * Normalize a value for storage. Right now we keep `@handle` as-is and
 * keep full URLs as-is — i.e., the storage format mirrors what the user
 * typed. If we ever want a strict canonical form, change this single
 * function and the rest of the UI updates for free.
 */
export function socialMediaStorageValue(_platform, value) {
  if (!value) return '';
  return String(value).trim();
}
